<template>
  <div class='provisioning-services-settings'>
    <dean-two-column-layout :title="headerTitle">
      <dean-section-title-item
          :icon="saveIcon"
          slot="title-items"
          :content="true"
          :large="true"
          @click.native="save"
      >
        Opslaan
      </dean-section-title-item>
      <div class="column" slot="left">
        <fade-transition group>
          <dean-form-row key="name" id="name" label="naam" v-model="data.name" type="text" :required-field="true"
                         :warning="$v.data.name.$error" @focus="$v.data.name.$touch()"/>

          <dean-form-row key="commercialName" id="commercialName" label="andelsnaam" v-model="data.commercialName" type="text" :required-field="false"
                         :warning="$v.data.commercialName.$error" @focus="$v.data.commercialName.$touch()"/>

          <dean-input-switch-form-row key="isActive" id="isActive" label="actief" :required="false"
                                      :value="data.isActive" @checked="inputSwitchChanged"></dean-input-switch-form-row>

          <dean-form-row key="sort" id="sort" label="soort" v-model="data.sort" type="number" min="1" max="100" :required-field="true"
                         :warning="$v.data.sort.$error" @focus="$v.data.sort.$touch()"/>

          <dean-multi-select-form-row id="bundles" key="bundles" label="Bundles" placeholder="kies bundles"
                                      :multiple="true" :options="availableBundlesList" :value="selectedBundlesList"
                                      @select="addBundle" @remove="removeBundle"/>
        </fade-transition>
      </div>
    </dean-two-column-layout>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-property-decorator';
import {
  deanSectionTitleItem,
  saveIcon,
} from '@deanone/dean-components';
import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
import deanInputSwitchFormRow from '@/components/deanComponents/deanInputSwitchFormRow.vue';
import deanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
import deanMultiSelectFormRow from '@/components/deanComponents/deanMultiSelectFormRow.vue';
import {FadeTransition} from 'vue2-transitions';
import {validationMixin} from 'vuelidate';
import {required} from 'vuelidate/lib/validators';
import {Validations} from 'vuelidate-property-decorators';
import {createNotification} from '@/mixins/validations';

@Component({
  components: {
    deanInputSwitchFormRow,
    deanSectionTitleItem,
    DeanFormRow,
    DeanTwoColumnLayout,
    deanSelectFormRow,
    deanMultiSelectFormRow,
    FadeTransition,
  },
  mixins: [validationMixin],
})
export default class Details extends Vue {
  // DATA
  public data: any = {
    name: '',
    commercialName: '',
    isActive: false,
    sort: '',
    bundles: [],
  };
  public name: string = '';
  public saveIcon: any = saveIcon;

  public bundles: Array<{
    value: string,
    label: string,
  }> = [];
  public availableBundles: Array<{
    value: string,
    label: string,
  }> = [];
  public selectedBundles: Array<{
    value: string,
    label: string,
  }> = [];

  // COMPUTED
  @Validations()
  get validations() {
    return {
      data: {
        name: {required},
        commercialName: {},
        isActive: {},
        sort: {required},
      },
    };
  }

  get headerTitle(): string {
    return (this.$route.params.id !== undefined) ? 'Provisioning Service wijzigen' : 'Provisioning Service toevoegen';
  }

  get availableBundlesList() {
    return this.availableBundles;
  }

  get selectedBundlesList() {
    this.updateAvailableBundles();
    return this.selectedBundles;
  }

  get sideMenu(): object {
    return {
      content: {
        title: {
          text: 'diensten ' + (this.name ? this.name : 'toevoegen'),
        },
        type: 'themed',
        sections: {
          general: {
            items: {
              backGroup: {
                title: 'Terug naar provisioning services',
                name: 'standingData.provisioningServices',
              },
              backCommissions: {
                title: 'Terug naar producten',
                name: 'standingData.products',
              },
            },
          },
        },
      },
    };
  }

  public save() {
    this.$v.$touch();
    if (!this.$v.$invalid) {
      this.$store.dispatch('setLoadOverlay', true);

      // Extract only bundle IDs for the payload
      const bundleIds = this.selectedBundles.map((bundle: any) => bundle.value);

      this.$store.dispatch('processProvisioningService', {
        identifier: this.$route.params.id,
        name: this.data.name,
        commercialName: this.data.commercialName,
        isActive: this.data.isActive,
        sort: this.data.sort,
        bundles: bundleIds,
      }).then(
          (response: any) => {
            this.$toast(this.$store.state.main.notification.messages.dataSaved);
          },
          (error: any) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
          },
      ).finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    } else {
      this.$toast(createNotification(this.$v));
    }
  }

  public created() {
    this.$store.dispatch('setLoadOverlay', true);

    let serviceBundleIds: string[] = [];

    // Fetch provisioning service info if editing
    const fetchServicePromise = this.$route.params.id
      ? this.$store.dispatch('requestProvisioningService', {id: this.$route.params.id})
          .then(
              (response: any) => {
                this.data.name = response.data.name;
                this.data.commercialName = response.data.commercialName;
                this.data.isActive = response.data.isActive;
                this.data.sort = response.data.sort;

                // Bundles is now just an array of IDs
                serviceBundleIds = response.data.bundles ? response.data.bundles : [];

                this.name = this.data.name;
              },
              (error) => {
                this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
              },
          )
      : Promise.resolve();

    // Fetch all bundle products (products with isPbxBundle=true)
    const fetchBundlesPromise = this.$store.dispatch('requestBundleProducts')
        .then(
            (response: any) => {
              this.bundles = response.data.data.map((product: any) => {
                const serviceName = product.serviceName ? product.serviceName : '';
                const publicName = product.publicName ? product.publicName : '';
                const displayName = serviceName && publicName ? `${serviceName} ${publicName}` : serviceName || publicName;

                return {
                  value: product.id,
                  label: displayName,
                };
              });
            },
            (error: any) => {
              this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
            },
        );

    // Wait for both requests to complete, then match selected bundles
    Promise.all([fetchServicePromise, fetchBundlesPromise])
        .finally(() => {
          // Match selected bundle IDs with fetched bundles to get correct labels
          if (serviceBundleIds.length > 0) {
            this.selectedBundles = this.bundles.filter((bundle: any) =>
              serviceBundleIds.includes(bundle.value)
            );
          }

          this.$store.commit('SET_SIDEMENU', this.sideMenu);
          this.$store.dispatch('setLoadOverlay', false);
        });
  }

  public inputSwitchChanged(field: { id: any, value: any }) {
    const key: string = field.id;
    // @ts-ignore-next-line
    this.data[key] = field.value;
  }

  public addBundle(field: any) {
    this.selectedBundles.push(field);
    this.updateAvailableBundles();
  }

  public removeBundle(field: any) {
    this.selectedBundles = this.selectedBundles.filter((bundle: any) => {
      if (bundle.value !== field.value) {
        return bundle;
      }
    });
    this.updateAvailableBundles();
  }

  public updateAvailableBundles() {
    if (this.selectedBundles.length <= 0) {
      this.availableBundles = this.bundles;
      return;
    }

    const selectedBundles: any[] = [];

    this.selectedBundles.forEach((bundle: any) => {
      selectedBundles[bundle.value] = bundle.label;
    });

    this.availableBundles = this.bundles.filter((bundle: any) => {
      if (selectedBundles[bundle.value] === undefined) {
        return bundle;
      }
    });
  }
}
</script>

<style lang="scss" scoped>

</style>
